import React, { useState } from "react";
import axios from "axios";
import { X, Loader2, Upload, Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 1024 * 1024;

const SPECS = [
  ["Description", "Text", "Required, up to 120 characters; becomes the Element name"],
  ["UOM", "Text", "Optional, e.g. each, sq ft"],
  ["Cost Per UOM", "Dollars", "0 or more, e.g. 1250 or $1,250.00"],
  ["Quantity", "Number", "Greater than 0"],
  ["ULWN", "Number (whole years)", "1–100"],
  ["RUL", "Number (whole years)", "0 up to ULWN"],
  ["Group / Category", "Text (optional column)", "Up to 60 characters; leave the column off or cells blank for Uncategorized"],
];

// RFC-4180 CSV parser — quotes, escaped quotes, CRLF/LF, embedded newlines.
function parseCsv(text) {
  const rows = [];
  let row = [], cell = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') q = false;
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(cell); rows.push(row); row = []; cell = "";
    } else cell += c;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

// Excel "CSV UTF-8" is UTF-8; plain Windows "CSV" is cp1252.
async function readText(file) {
  const buf = await file.arrayBuffer();
  try { return new TextDecoder("utf-8", { fatal: true }).decode(buf); }
  catch { return new TextDecoder("windows-1252").decode(buf); }
}

const money = (n) => {
  const v = Number(n);
  const cents = Math.round(v * 100) % 100 !== 0;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: 2 })}`;
};

export default function ImportElementsModal({ aid, onImported, onClose }) {
  const [fileName, setFileName] = useState("");
  const [payload, setPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError(null); setPreview(null); setPayload(null); setDone(null);
    setFileName(f.name);
    if (!/\.csv$/i.test(f.name)) return setError("File must be a .csv file");
    if (f.size > MAX_BYTES) return setError("File is larger than 1 MB");
    const rows = parseCsv((await readText(f)).replace(/^\ufeff/, ""));
    if (!rows.length) return setError("File is empty");
    const body = { header: rows[0], rows: rows.slice(1), dry_run: true };
    setBusy(true);
    try {
      const res = await axios.post(`${API}/associations/${aid}/elements/import`, body);
      setPayload(body);
      setPreview(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API}/associations/${aid}/elements/import`, { ...payload, dry_run: false });
      setDone(res.data);
      await onImported(res.data.bundle);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const fileErrors = preview?.file_errors || [];
  const canImport = preview && !fileErrors.length && preview.error_count === 0 && preview.valid_count > 0 && !done;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={onClose}
      data-testid="import-elements-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-navy shadow-2xl w-[760px] max-h-[88vh] flex flex-col rounded-[8px]"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-rule bg-navy-wash">
          <div className="text-[13px] font-semibold text-navy">Import Elements</div>
          <button type="button" onClick={onClose} className="text-slate hover:text-coral" data-testid="import-elements-close">
            <X size={14} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto text-[12px] flex flex-col gap-3">
          <Specs />

          <div className="flex items-center gap-3">
            <label
              className={`inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-1 text-[12px] font-semibold rounded-[3px] ${done ? "opacity-40 pointer-events-none" : "cursor-pointer"}`}
              data-testid="import-file-label"
            >
              <Upload size={12} /> Choose CSV File
              <input type="file" accept=".csv,text/csv" onChange={pick} className="hidden" data-testid="import-file-input" />
            </label>
            <span className="text-slate truncate" data-testid="import-file-name">{fileName || "No file selected"}</span>
            {busy && <Loader2 size={13} className="animate-spin text-navy" />}
          </div>

          {(error || fileErrors.length > 0) && (
            <div className="px-3 py-2 bg-[#FEF5F2] border border-coral/40 text-coral text-[11.5px] rounded-[3px]" data-testid="import-file-error">
              {error || fileErrors.join(" · ")}
            </div>
          )}

          {preview && !fileErrors.length && !done && <Preview preview={preview} />}

          {done && (
            <div className="px-3 py-2 bg-[#E4F5EA] border border-[#0F7A55]/40 text-[#0F7A55] text-[12.5px] font-semibold rounded-[3px] flex items-center gap-2" data-testid="import-success">
              <Check size={14} /> {done.imported} Elements imported · {money(done.total_cost)} total
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-rule bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 border border-rule bg-white text-slate hover:border-navy hover:text-navy text-[12px]"
            data-testid="import-cancel"
          >
            {done ? "Close" : "Cancel"}
          </button>
          {!done && (
            <button
              type="button"
              onClick={commit}
              disabled={!canImport || busy}
              className="px-3 py-1 bg-navy text-white hover:bg-[#12406B] disabled:opacity-40 text-[12px] font-semibold flex items-center gap-1"
              data-testid="import-submit"
            >
              {busy && payload && <Loader2 size={12} className="animate-spin" />}
              Import{preview?.valid_count && canImport ? ` ${preview.valid_count} Elements` : ""}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Specs() {
  return (
    <div data-testid="import-specs">
      <div className="text-[10.5px] tracking-[0.08em] uppercase font-semibold text-navy mb-1">File Requirements</div>
      <ul className="list-disc pl-4 text-ink/85 leading-snug mb-2">
        <li><b>File type:</b> CSV (.csv). In Excel or Google Sheets use <i>Save As → CSV</i>.</li>
        <li><b>Row 1</b> must be the first 6 column names below, in this exact order. A 7th <b>Group / Category</b> column is optional. Elements start on row 2, one per row.</li>
      </ul>
      <table className="w-full border border-rule text-[11.5px]">
        <thead className="bg-navy text-white">
          <tr>
            <th className="px-2 py-1 text-left w-6">#</th>
            <th className="px-2 py-1 text-left">Column Name</th>
            <th className="px-2 py-1 text-left">Format</th>
            <th className="px-2 py-1 text-left">Rule</th>
          </tr>
        </thead>
        <tbody>
          {SPECS.map(([name, fmt, rule], i) => (
            <tr key={name} className="border-t border-rule">
              <td className="px-2 py-0.5 font-mono">{i + 1}</td>
              <td className="px-2 py-0.5 font-semibold text-navy">{name}</td>
              <td className="px-2 py-0.5">{fmt}</td>
              <td className="px-2 py-0.5 text-slate">{rule}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-slate text-[11px] mt-1">Blank rows are skipped · Up to 500 rows · Imported Elements start with Include unchecked · Nothing is imported until every row passes.</div>
    </div>
  );
}

function Preview({ preview }) {
  const { rows, valid_count, error_count, total_cost } = preview;
  return (
    <div data-testid="import-preview">
      <div className={`text-[12px] font-semibold mb-1 ${error_count ? "text-coral" : "text-[#0F7A55]"}`} data-testid="import-preview-summary">
        {error_count
          ? `${error_count} of ${rows.length} rows need fixing — correct the file and choose it again.`
          : `All ${valid_count} rows passed · ${money(total_cost)} total`}
      </div>
      <div className="max-h-[260px] overflow-y-auto border border-rule">
        <table className="w-full text-[11.5px]">
          <thead className="bg-navy-wash text-navy sticky top-0">
            <tr>
              <th className="px-2 py-1 text-left w-12">Row</th>
              <th className="px-2 py-1 text-left w-6" />
              <th className="px-2 py-1 text-left">Description</th>
              <th className="px-2 py-1 text-left">Result</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.row} className={`border-t border-rule ${r.errors.length ? "bg-[#FEF5F2]" : ""}`} data-testid={`import-row-${r.row}`}>
                <td className="px-2 py-0.5 font-mono">{r.row}</td>
                <td className="px-2 py-0.5">
                  {r.errors.length ? <AlertCircle size={12} className="text-coral" /> : <Check size={12} className="text-[#0F7A55]" />}
                </td>
                <td className="px-2 py-0.5 truncate max-w-[220px]">{r.name || "—"}</td>
                <td className={`px-2 py-0.5 ${r.errors.length ? "text-coral" : "text-slate font-mono"}`}>
                  {r.errors.length
                    ? r.errors.join("; ")
                    : `${r.values.quantity.toLocaleString()} ${r.values.uom || ""} × ${money(r.values.current_cost)} · ${r.values.useful_life_when_new}/${r.values.remaining_useful_life} yrs${r.values.category ? ` · ${r.values.category}` : ""}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
